// Bootstrap: open the Discord (or mock) session, build the UI, run the ceremony controller.
//
// Boot diagnostics: index.html pre-registers window error handlers + __paintBoot, and every
// handshake stage paints a line onto the page (the iframe console is near-unreachable inside
// Discord). #boot is removed once the session opens; a fatal error keeps it on screen.

import './styles.css';
import { installErrorPainters, markMainRan, paintBoot, removeBootLog } from './boot';
import { DISCORD_CLIENT_ID, IS_DISCORD } from './config';
import { GUILD_GATE_LINE } from './copy';
import { GuildNotAllowedError, startSession } from './sdk';
import { ArcadeUI } from './ui';
import { Arcade } from './state';

// Injected by vite.config.ts `define` at build time — the deploy-cache canary.
declare const __BUILD_TS__: string;

async function main() {
  markMainRan();
  installErrorPainters();
  const build = typeof __BUILD_TS__ === 'string' ? __BUILD_TS__ : 'dev';
  paintBoot(
    `[main] bundle running; build=${build}; mode=${IS_DISCORD ? 'discord' : 'mock'}; client_id=${DISCORD_CLIENT_ID}`,
  );
  const root = document.getElementById('app');
  if (!root) throw new Error('#app root missing');
  const ui = new ArcadeUI(root);
  // A stalled handshake resolves nothing and throws nothing — the watchdog turns that
  // silence into a readable line ([sdk] stages above it show where it stalled).
  const watchdog = window.setTimeout(() => {
    paintBoot('[main] 10s in the handshake and still waiting — the last [sdk] line above is where it stalled');
  }, 10_000);
  try {
    const session = await startSession();
    window.clearTimeout(watchdog);
    paintBoot('[main] session open — entering the Arcade');
    removeBootLog();
    const arcade = new Arcade(session, ui);
    await arcade.load();
  } catch (err) {
    window.clearTimeout(watchdog);
    paintBoot(`[main] fatal: ${err instanceof Error ? (err.stack ?? err.message) : String(err)}`);
    ui.fatal(
      err instanceof GuildNotAllowedError
        ? GUILD_GATE_LINE
        : 'The Arcade could not open. Reopen the Activity from Discord.',
    );
    console.error(err);
  }
}

void main();
